import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const ExperienceDetailsModal = ({
  open,
  handleClose,
  experience,
  onEdit,
  onDelete,
}) => {
  if (!experience) return null;

  const title = experience.title || experience.name || 'Untitled Experience';
  const category = experience.category || 'Curated Experience';
  const location = experience.location || experience.venue || '';
  const city = experience.city || '';
  const duration = experience.duration || 'Flexible';
  const pricing = experience.pricing ?? experience.price ?? experience.amount ?? 0;
  const discountPrice = experience.discountPrice ?? experience.offerPrice;
  const slots = experience.slots ?? experience.capacity ?? experience.totalSlots ?? '—';
  const overview = experience.overview || experience.description || 'No overview provided.';
  const itinerary = experience.itinerary || '';
  const guidelines = experience.guidelines || experience.terms || '';
  const active = experience.active ?? experience.isActive ?? true;
  const imageUrl = experience.imageUrl || experience.image || experience.banner || '/experience_placeholder.jpg';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.2)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 0,
          position: 'relative',
        }}
      >
        {/* Banner Image */}
        <Box
          sx={{
            height: 240,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            bgcolor: '#0f172a',
          }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt={title}
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&auto=format&fit=crop&q=80';
            }}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'brightness(0.85)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.3) 0%, rgba(15, 23, 42, 0.85) 100%)',
            }}
          />
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: '#fff',
              bgcolor: 'rgba(0,0,0,0.4)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ position: 'absolute', bottom: 20, left: 24, right: 24 }}>
            <Stack direction="row" spacing={1} mb={1}>
              <Chip
                icon={<CategoryOutlinedIcon sx={{ fontSize: 16, color: '#fff !important' }} />}
                label={category}
                size="small"
                sx={{
                  bgcolor: alpha('#fff', 0.2),
                  color: '#fff',
                  fontWeight: 700,
                  backdropFilter: 'blur(8px)',
                }}
              />
              <Chip
                label={active ? 'Active' : 'Inactive'}
                size="small"
                sx={{
                  bgcolor: active ? alpha('#16a34a', 0.85) : alpha('#dc2626', 0.85),
                  color: '#fff',
                  fontWeight: 700,
                }}
              />
            </Stack>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.02em' }}>
              {title}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3.5, bgcolor: '#f8fafc' }}>
        {/* Quick Highlights */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>
              <Stack direction="row" spacing={1} alignItems="center" color="#64748b" mb={0.5}>
                <LocationOnOutlinedIcon fontSize="small" sx={{ color: '#2563eb' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Location
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                {city ? `${location ? location + ', ' : ''}${city}` : location || '—'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>
              <Stack direction="row" spacing={1} alignItems="center" color="#64748b" mb={0.5}>
                <AccessTimeOutlinedIcon fontSize="small" sx={{ color: '#0891b2' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Duration
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                {duration}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>
              <Stack direction="row" spacing={1} alignItems="center" color="#64748b" mb={0.5}>
                <PeopleAltOutlinedIcon fontSize="small" sx={{ color: '#7c3aed' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Slots / Capacity
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                {slots} spots
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.06)' }}>
              <Stack direction="row" spacing={1} alignItems="center" color="#64748b" mb={0.5}>
                <CurrencyRupeeOutlinedIcon fontSize="small" sx={{ color: '#16a34a' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Pricing
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#16a34a' }}>
                  ₹{Number(discountPrice || pricing).toLocaleString('en-IN')}
                </Typography>
                {discountPrice && discountPrice < pricing && (
                  <Typography variant="caption" sx={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                    ₹{Number(pricing).toLocaleString('en-IN')}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* Overview */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
            Overview
          </Typography>
          <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {overview}
          </Typography>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Itinerary */}
        {itinerary && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
              Itinerary & Schedule
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
              {itinerary}
            </Typography>
          </Box>
        )}

        {/* Guidelines */}
        {guidelines && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', mb: 1 }}>
              Guidelines & Terms
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
              {guidelines}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#fff', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        {onDelete && (
          <Button
            onClick={() => {
              handleClose();
              onDelete(experience);
            }}
            startIcon={<DeleteOutlineIcon />}
            sx={{
              color: '#dc2626',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '12px',
              mr: 'auto',
            }}
          >
            Delete
          </Button>
        )}
        {onEdit && (
          <Button
            onClick={() => {
              handleClose();
              onEdit(experience);
            }}
            startIcon={<EditOutlinedIcon />}
            variant="outlined"
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              color: '#11409B',
              borderColor: alpha('#11409B', 0.3),
            }}
          >
            Edit Experience
          </Button>
        )}
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #0c3483 0%, #11409B 100%)',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExperienceDetailsModal;
